import { useTablePaginationManager } from "@powerledger/ui-component-lib";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { notifications } from "@/app/container/notifications";
import { AppUserAccountInfo, useAppContext } from "@/app/context";
import { useActiveRegistryDisplayName } from "@/app/hooks/use-active-registry-display-name/use-active-registry-display-name";
import { useAllRecOptions } from "@/app/hooks/use-all-rec-options";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { formatAttributes, ReturnTypeFormatAttributes } from "@/app/lib/format-attributes";
import { RecHoldingsTableData } from "@/app/pages/recs/rec-holdings/rec-holdings.types";
import {
  AccountType,
  AssetAssignmentStatus,
  AssetType,
  CertificateType,
  RecAttributeInput,
  RecAttributes,
  RegistryCode,
  SortOrderInput,
  TradeContractAssetStatus,
  TradeContractStatus,
  useAssignAssetsMutation,
  usePaginatedHoldingsQuery,
  useTradeContractAssetsByTradeContractIdQuery,
} from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { appendErcotVintageHalvesToMretsVintagesFilterValues } from "../page-header";
import {
  AssignAssetsFormValues,
  ForwardTradeRecsAssignModalProps,
  RecAssetAttributes,
  RecAssignmentUnSupportedColumns,
  TradeContractAssetsTableData,
} from "./forward-trade-recs-assign-modal.types";

const getRegistryCodeForHoldingsQuery = (
  tradeContractRegistryCode: AggregatedRegistryCode,
  linkedRegistries: AppUserAccountInfo["linkedRegistries"],
) => {
  if (
    tradeContractRegistryCode === ANY_REGISTRY_CODE &&
    linkedRegistries?.some((registry) => registry.accountType === AccountType.NonRegistry)
  ) {
    return linkedRegistries?.find((registry) => registry.accountType === AccountType.Registry)
      ?.registryCode as TraceXSupportedRegistries;
  }
  return tradeContractRegistryCode;
};

export const useForwardTradeRecsAssignModal = ({
  visible,
  closeModal,
  tradeContractNumber,
  tradeContractId,
  askTradeVolume,
  tradeAttributes,
  status,
  assetAssignmentStatus,
  tradeContractRegistryCode,
}: ForwardTradeRecsAssignModalProps) => {
  const { t } = useTranslation();
  const {
    toggles: { enableCrossRegistryForwardTrading },
    loading: featureTogglesLoading,
  } = useAllFeatureToggleValues();
  const { userAccountInfo } = useAppContext();

  const followErcotNamingConvention =
    !enableCrossRegistryForwardTrading && tradeContractRegistryCode === RegistryCode.Ercot;

  const registryCodeForHoldingsQuery = getRegistryCodeForHoldingsQuery(
    tradeContractRegistryCode,
    userAccountInfo?.linkedRegistries,
  );

  const { getRegistryDisplayName } = useActiveRegistryDisplayName();
  const { recOptionsByRegistryCode, loading: recOptionsLoading } = useAllRecOptions();

  const { pageInfo, offset, fetchData, sort, setTotalItems } = useTablePaginationManager<
    RecHoldingsTableData,
    SortOrderInput[]
  >({});

  const {
    data: recsHoldingData,
    loading: recsHoldingLoading,
    refetch: recsHoldingRefetch,
  } = usePaginatedHoldingsQuery({
    fetchPolicy: "cache-and-network",
    skip:
      assetAssignmentStatus === AssetAssignmentStatus.Assigned ||
      ![TradeContractStatus.Contracted, TradeContractStatus.PendingComplete].includes(status) ||
      registryCodeForHoldingsQuery === undefined ||
      featureTogglesLoading,
    variables: {
      input: {
        offSetPaginationInfo: {
          offset,
          limit: pageInfo.pageSize,
        },
        sortOrderInputs: sort,
        where: {
          registryCode: registryCodeForHoldingsQuery,
          [registryCodeForHoldingsQuery === ANY_REGISTRY_CODE ? "normalizedRecAttributes" : "recAttributes"]: {
            eligibilities: tradeAttributes.eligibilities,
            certifications: tradeAttributes.certifications,
            locations: tradeAttributes.locations,
            fuelSources: tradeAttributes.fuelSources,
            vintages: appendErcotVintageHalvesToMretsVintagesFilterValues(
              tradeAttributes.vintages ?? [],
              tradeContractRegistryCode,
            ),
            projects: tradeAttributes.projects,
            commencementDate: tradeAttributes.commencementDate,
            certificateTypes: tradeAttributes.certificateTypes as CertificateType[],
          },
        },
      },
    },
    onCompleted(data) {
      setTotalItems(data?.paginatedHoldings?.offsetInfo?.total ?? 0);
    },
  });

  const recsHoldingTableData = useMemo(() => {
    const formmatedRecHoldingData: RecHoldingsTableData<RecAssetAttributes>[] = [];
    if (!recsHoldingData?.paginatedHoldings.holdings) return formmatedRecHoldingData;
    for (const recHolding of recsHoldingData.paginatedHoldings.holdings) {
      const recOptions = recOptionsByRegistryCode[recHolding.registryCode as TraceXSupportedRegistries];
      const attributes = recHolding.attributes as RecAssetAttributes;
      const formattedAttributes = recOptions
        ? formatAttributes({
            attributes,
            options: recOptions,
          })
        : ({} as ReturnTypeFormatAttributes);
      formmatedRecHoldingData.push({
        ...recHolding,
        attributes,
        ...formattedAttributes,
        registryDisplayName: getRegistryDisplayName(recHolding.registryCode as AggregatedRegistryCode),
      });
    }
    return formmatedRecHoldingData;
  }, [recsHoldingData?.paginatedHoldings?.holdings, recOptionsByRegistryCode, getRegistryDisplayName]);

  const reloadRecsHolding = useCallback(() => {
    recsHoldingRefetch();
  }, [recsHoldingRefetch]);

  const {
    data: contractAssetsData,
    loading: contractAssetsLoading,
    refetch: contractAssetsRefetch,
  } = useTradeContractAssetsByTradeContractIdQuery({
    fetchPolicy: "cache-and-network",
    skip: tradeContractRegistryCode === undefined || featureTogglesLoading,
    variables: {
      tradeContractId,
      registryCode: tradeContractRegistryCode,
    },
  });

  const [assignAssetsToTradeContract] = useAssignAssetsMutation();

  const contractAssetsTableData = useMemo(() => {
    const tradeContractAssetsTableData: TradeContractAssetsTableData[] = [];

    if (!contractAssetsData?.tradeContractAssetsByTradeContractId) return tradeContractAssetsTableData;
    for (const tradeContractAsset of contractAssetsData.tradeContractAssetsByTradeContractId) {
      const recOptions = recOptionsByRegistryCode[tradeContractAsset.registryCode as TraceXSupportedRegistries];
      const attributes = tradeContractAsset.attributes as RecAttributes;
      const formattedAttributes = recOptions
        ? formatAttributes({
            attributes,
            options: recOptions,
          })
        : ({} as ReturnTypeFormatAttributes);
      tradeContractAssetsTableData.push({
        ...formattedAttributes,
        ...tradeContractAsset,
        attributes,
        registryDisplayName: getRegistryDisplayName(tradeContractAsset.registryCode as AggregatedRegistryCode),
      });
    }
    return tradeContractAssetsTableData;
  }, [recOptionsByRegistryCode, contractAssetsData?.tradeContractAssetsByTradeContractId, getRegistryDisplayName]);

  const reloadTradeContractAssest = useCallback(() => {
    contractAssetsRefetch();
  }, [contractAssetsRefetch]);

  const formikProps = useMemo(() => {
    const assetsToAssign =
      recsHoldingTableData?.map(({ attributes, availableBalance, registryCode }) => {
        const { certifications = [], fuelSources = [], project = null, vintage, vintageHalf } = attributes;
        const recAttributes: RecAttributeInput = {
          certifications,
          commencementDate: attributes.commencementDate,
          locations: attributes.location ? [attributes.location] : undefined,
          ...(attributes.__typename === "MretsRecAttributes"
            ? {
                eligibilities: attributes.eligibilities ?? [],
              }
            : {}),
          certificateTypes: attributes.certificateType ? [attributes.certificateType] : undefined,
          fuelSources,
          projects: project ? [project] : undefined,
          vintages: [`${vintage}-${vintageHalf}`],
        };

        return {
          recAttributes,
          volume: 0,
          // Adding property to track the maximum assignable RECs
          availableVolume: availableBalance,
          registryCode,
        };
      }) ?? [];

    const initialValues: AssignAssetsFormValues = {
      assetType: AssetType.Rec,
      tradeContractId: tradeContractId,
      assetsToAssign,
    };

    return { initialValues };
  }, [recsHoldingTableData, tradeContractId]);

  const onAssignRecs = useCallback(
    async (values: AssignAssetsFormValues) => {
      try {
        const processedValues = {
          ...values,
          assetsToAssign: values.assetsToAssign
            .filter(({ volume }) => Number(volume) > 0)
            .map(({ availableVolume, volume, ...rest }) => ({
              ...rest,
              volume: Number(volume),
            })),
        };

        const { data } = await assignAssetsToTradeContract({
          variables: {
            input: {
              ...processedValues,
            },
          },
        });
        reloadTradeContractAssest();
        reloadRecsHolding();
        if (data?.assignAssetsToTradeContract) {
          notifications.info({ description: t("RECs are currently being assigned.") });
        }
      } catch (error) {
        error instanceof Error &&
          notifications.error({
            description: t(error?.message),
          });
      }
    },
    [t, assignAssetsToTradeContract, reloadRecsHolding, reloadTradeContractAssest],
  );

  // While assigning RECs if locking fails i.e "LockFailed" status,
  // filter them before adding to the total assigned RECs.
  const totalAssignedRecs = useMemo(
    () =>
      contractAssetsTableData
        .filter((item) => item.status !== TradeContractAssetStatus.LockFailed)
        .reduce((total, item) => total + (item.volume || 0), 0),
    [contractAssetsTableData],
  );

  //original ask volume subtract with the total assets assigned volume
  const currentRequiredVolume = useMemo(() => {
    return askTradeVolume - totalAssignedRecs;
  }, [askTradeVolume, totalAssignedRecs]);

  //Only Displayed in action "Assign RECs"
  //Show Assign RECs Table when the asset assignment status is not assigned and the status is contracted or pending complete and the current required volume is greater than 0
  const showAssignRECsTable = useMemo(
    () =>
      assetAssignmentStatus === AssetAssignmentStatus.NotAssigned &&
      (status === TradeContractStatus.Contracted || status === TradeContractStatus.PendingComplete) &&
      currentRequiredVolume > 0,
    [status, currentRequiredVolume, assetAssignmentStatus],
  );

  // Displayed in both action "Assign RECs" (when RECs are being assigned)
  // and "View RECs" (for already assigned RECs).
  const showViewRECsTable = useMemo(
    () => assetAssignmentStatus === AssetAssignmentStatus.Assigned || totalAssignedRecs > 0,
    [totalAssignedRecs, assetAssignmentStatus],
  );

  const hiddenColumns = useMemo(() => {
    if (!registryCodeForHoldingsQuery || featureTogglesLoading) return [];
    const { unsupportedColumns = [] } = RecAssignmentUnSupportedColumns[registryCodeForHoldingsQuery];
    return unsupportedColumns;
  }, [registryCodeForHoldingsQuery, featureTogglesLoading]);

  return {
    visible,
    closeModal,
    contractAssetsTableData,
    recsHoldingTableData,
    tradeContractNumber,
    recOptionsByRegistryCode,
    contractAssetsLoading: contractAssetsLoading || recOptionsLoading,
    recsHoldingLoading: recOptionsLoading || recsHoldingLoading,
    reloadTradeContractAssest,
    formikProps,
    onAssignRecs,
    pageInfo,
    fetchData,
    reloadRecsHolding,
    currentRequiredVolume,
    showAssignRECsTable,
    showViewRECsTable,
    totalAssignedRecs,
    followErcotNamingConvention,
    hiddenColumns,
    enableCrossRegistryForwardTrading,
  };
};
